import { stableKeyOrder } from "./stagingModel";

export type SchemaEncoding = "avro" | "json";

// Mirror of old /studio's schema-encoding detection (GuardrailsPanel /
// usePlaygroundHooks): a JSON-Schema-shaped document ($schema marker, or an
// object type with properties) is "json"; any other valid JSON counts as
// Avro; unparsable content has no encoding.
function looksLikeJsonSchema(parsed: unknown): boolean {
  if (!parsed) return false;
  if (typeof parsed !== "object") return false;
  const doc = parsed as Record<string, unknown>;
  if (doc.$schema) return true;
  if (doc.type !== "object") return false;
  return Boolean(doc.properties);
}

export function detectSchemaEncoding(content: string): SchemaEncoding | null {
  try {
    return looksLikeJsonSchema(JSON.parse(content)) ? "json" : "avro";
  } catch {
    return null;
  }
}

// JSON Schema's "required" is a SET — the engine's introspection emits it
// in hash order, so comparison sorts those arrays (and only those; other
// arrays like prefixItems are order-sensitive).
function withRequiredSorted(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(withRequiredSorted);
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        key,
        key === "required" && Array.isArray(entry)
          ? [...entry].sort()
          : withRequiredSorted(entry),
      ]),
    );
  }
  return value;
}

// One definition normalized for content comparison: parsed JSON with keys
// stably ordered and required-sets sorted (formatting, key order and
// required order don't change the shape), the raw trimmed text when it
// isn't JSON.
function normalizedDefinition(text: string): string {
  try {
    return JSON.stringify(stableKeyOrder(withRequiredSorted(JSON.parse(text))));
  } catch {
    return text.trim();
  }
}

// Whether two schema definitions describe the exact same shape — the
// seeded-draft Save disables against an identically-shaped stored schema
// (saving it again would only mint a duplicate). Empty text matches
// nothing: a blank draft is not "already saved".
export function schemaContentEqual(draft: string, stored: string): boolean {
  if (draft.trim() === "" || stored.trim() === "") return false;
  return normalizedDefinition(draft) === normalizedDefinition(stored);
}
